// RMDV Design Language component specs (DESIGN_LANGUAGE.md §4).
// Spread LAST in AppTheme so these fully replace the stock MUI-template overrides
// that produced the glossy gradient buttons, grey cards, mixed chips, etc.
//
// IMPORTANT (dark mode): this is a CSS-variables theme
// (AppTheme: cssVariables.colorSchemeSelector). Inside a styleOverrides callback,
// `theme.palette.*` is a STATIC snapshot of the DEFAULT (light) scheme — it does
// NOT switch when the runtime scheme flips to dark, so it renders light-on-dark.
// Always read colours via `(theme.vars || theme).palette.*` (CSS var, follows the
// runtime scheme) and branch on scheme with `theme.applyStyles('dark', {...})`,
// never `theme.palette.mode === 'dark'` (also frozen to light).
 

// Palette colour group via CSS vars so .main/.dark/.light/.contrastText follow
// the runtime scheme.
const pal = (theme, color) => {
  const p = (theme.vars || theme).palette;
  return p[color] || p.primary;
};

export const brandComponents = {
  // Flat, legible buttons (fixes V3 glossy gradients, V4 unreadable disabled).
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: 14,
        borderRadius: 6,
        boxShadow: 'none',
        height: 36,
        paddingLeft: 14,
        paddingRight: 14,
        minWidth: 0,
        '&:hover': { boxShadow: 'none' },
      },
      sizeSmall: { height: 28, paddingLeft: 10, paddingRight: 10, fontSize: 13 },
      sizeLarge: { height: 40, paddingLeft: 18, paddingRight: 18 },
      contained: ({ theme, ownerState }) => {
        const c = pal(theme, ownerState.color);
        return {
          backgroundImage: 'none',
          backgroundColor: c.main,
          color: c.contrastText || '#fff',
          border: '1px solid transparent',
          '&:hover': { backgroundColor: c.dark },
          '&:active': { backgroundColor: c.dark },
          '&.Mui-disabled': {
            backgroundColor: (theme.vars || theme).palette.action.selected,
            color: c.main,
            opacity: 0.7,
            border: '1px solid transparent',
          },
        };
      },
      outlined: ({ theme }) => ({
        borderColor: (theme.vars || theme).palette.divider,
        color: (theme.vars || theme).palette.text.primary,
        backgroundColor: (theme.vars || theme).palette.background.paper,
        '&:hover': { backgroundColor: (theme.vars || theme).palette.action.hover, borderColor: (theme.vars || theme).palette.text.disabled },
        '&.Mui-disabled': { color: (theme.vars || theme).palette.text.disabled, borderColor: (theme.vars || theme).palette.divider },
      }),
      text: ({ theme }) => ({
        color: (theme.vars || theme).palette.text.secondary,
        '&:hover': { backgroundColor: (theme.vars || theme).palette.action.hover, color: (theme.vars || theme).palette.text.primary },
      }),
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 6,
        color: (theme.vars || theme).palette.text.secondary,
        '&:hover': { backgroundColor: (theme.vars || theme).palette.action.hover, color: (theme.vars || theme).palette.text.primary },
      }),
    },
  },

  // ONE badge/chip system (fixes V5). Subtle fill = color.light bg + color.main text.
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 6, height: 22, fontSize: 11.5, fontWeight: 600, border: 'none' },
      label: { paddingLeft: 8, paddingRight: 8 },
      sizeSmall: { height: 20, fontSize: 11 },
      filled: ({ theme, ownerState }) => {
        const named = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];
        if (named.includes(ownerState.color)) {
          const c = pal(theme, ownerState.color);
          return { backgroundColor: c.light, color: c.main };
        }
        return { backgroundColor: (theme.vars || theme).palette.action.hover, color: (theme.vars || theme).palette.text.secondary };
      },
      outlined: ({ theme }) => ({
        borderColor: (theme.vars || theme).palette.divider,
        color: (theme.vars || theme).palette.text.secondary,
        backgroundColor: 'transparent',
      }),
    },
  },

  // White cards on the grey canvas, 1px border, flat (fixes V7).
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.background.paper,
        backgroundImage: 'none',
        border: `1px solid ${(theme.vars || theme).palette.divider}`,
        borderRadius: 8,
        boxShadow: 'none',
        padding: 16,
        gap: 16,
      }),
    },
  },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },

  // Inputs — height 36, radius 6, crisp focus ring.
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 6,
        backgroundColor: (theme.vars || theme).palette.background.paper,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: (theme.vars || theme).palette.divider },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (theme.vars || theme).palette.text.disabled },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme.vars || theme).palette.primary.main,
          borderWidth: 1,
          boxShadow: `0 0 0 3px ${(theme.vars || theme).palette.action.selected}`,
        },
        // MUI Core v9 regression: an outlined <Select> (its OutlinedInput root also
        // carries `.MuiSelect-root`) no longer has an intrinsic height, so as a flex
        // child it STRETCHES to the tallest sibling (e.g. a labelled field beside it)
        // and reads far too tall — the "select boxes are the wrong height" report.
        // v7 rendered a fixed compact box; restore that by pinning the select height
        // (single-line only — plain/multiline text inputs are untouched). Do NOT set
        // alignSelf here: in a flex COLUMN that centres the select horizontally and
        // shoves it right (a v7-vs-v9 probe caught this) — leave alignSelf:auto so the
        // select follows its container's alignItems, exactly like v7.
        '&.MuiSelect-root .MuiSelect-select': { display: 'flex', alignItems: 'center' },
        '&.MuiSelect-root.MuiInputBase-sizeSmall': { height: 36 },
        '&.MuiSelect-root.MuiInputBase-sizeMedium': { height: 40 },
      }),
      input: { paddingTop: 8, paddingBottom: 8 },
    },
  },

  // Sidebar / nav active state (fixes V11): accent-subtle bg + accent text/icon.
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 6,
        minHeight: 36,
        '&:hover': { backgroundColor: (theme.vars || theme).palette.action.hover },
        '&.Mui-selected': {
          backgroundColor: (theme.vars || theme).palette.action.selected,
          color: (theme.vars || theme).palette.primary.main,
          '& .MuiListItemIcon-root': { color: (theme.vars || theme).palette.primary.main },
          '&:hover': { backgroundColor: (theme.vars || theme).palette.action.selected },
        },
      }),
    },
  },

  // Tabs — body/strong, active = text.primary + 2px accent underline.
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 40 },
      indicator: ({ theme }) => ({ backgroundColor: (theme.vars || theme).palette.primary.main, height: 2 }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        fontWeight: 500,
        fontSize: 14,
        minHeight: 40,
        minWidth: 0,
        padding: '0 2px',
        marginRight: 24,
        color: (theme.vars || theme).palette.text.secondary,
        '&.Mui-selected': { color: (theme.vars || theme).palette.text.primary },
      }),
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 8, fontSize: 13, alignItems: 'center', border: '1px solid transparent' },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: 'hsl(222, 28%, 14%)',
        ...theme.applyStyles('dark', { backgroundColor: 'hsl(224, 19%, 16%)' }),
        fontSize: 11.5,
        borderRadius: 6,
        padding: '6px 9px',
      }),
    },
  },

  MuiDivider: {
    styleOverrides: { root: ({ theme }) => ({ borderColor: (theme.vars || theme).palette.divider }) },
  },

  // Sidebar drawer must follow the colour scheme (the template hardcoded white
  // + applyStyles('dark'), which left the sidebar light in dark mode).
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.background.paper,
        backgroundImage: 'none',
        borderColor: (theme.vars || theme).palette.divider,
      }),
    },
  },

  // AppBar (mobile top bar) follows the scheme too.
  MuiAppBar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.background.paper,
        backgroundImage: 'none',
        color: (theme.vars || theme).palette.text.primary,
        boxShadow: 'none',
      }),
    },
  },
};
